import { map } from "rxjs/operators";
import { Component, Input } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from "@env";
import { TranslateService } from "@ngx-translate/core";
import { ValidationService } from "@app/core/services/validation.service";
import { Language } from "@app/core/enums/Language";
import { ImportDetail } from "@app/shared/CSDAModels/ImportDetail";
import { ImportResult } from "@app/shared/CSDAModels/ImportResult";
import { Observable, ReplaySubject } from "rxjs";
import Swal from "sweetalert2";
import { LanguageService } from "@app/core/services/language.service";

@Component({
  selector: 'app-upload-orders',
  templateUrl: './upload-orders.component.html',
  styleUrls: ['./upload-orders.component.scss'],
  standalone: false
})
export class UploadOrdersComponent {
  // 🔹 Values passed from ImportComponent
  @Input() isBatchEnabled: boolean = true;
  @Input() batchDescription: string | null = null;
  @Input() batchCarrier: string | null = null;
  @Input() batchServiceType: string | null = null;
  @Input() batchEmail: string | null = null;

  importOrdersDetail: ImportDetail;
  importOrdersResult: ImportResult;
  uploadProgress = 0;
  fileName: string | undefined;
  isProcessing: boolean = false;

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private validationService: ValidationService,
    private languageService: LanguageService,
  ) {}

  onSelectFileClick(event: MouseEvent): void {
  if (this.isBatchEnabled && (
      !this.batchDescription?.trim() ||
      !this.batchEmail?.trim()
    )) {

    // Stop the file picker from opening
    event.preventDefault();
    event.stopPropagation();

    Swal.fire({
      icon: 'warning',
      title: this.translateService.instant('app.batchShipments.missingParams'),
      confirmButtonText: 'OK',
      confirmButtonColor: '#354bdcff'
    });

    return;
  }
}
  //--------------------------
  // Selected file by the user
  //--------------------------
  selectFile(event: any): void {
    if (event.target.files && event.target.files[0]) {
      this.fileName = event.target.files[0].name;
      this.convertFile(event.target.files[0]).subscribe(base64 => {
        // 🔹 Construct upload detail with parent data
        this.importOrdersDetail = {
          IsDevelopmentEnvironment: false,
          Language: this.languageService.GetCurrentLanguage(),
          File: base64,
          IsDeleteAll: false,
          // ✅ include parent-provided metadata
          Description: this.batchDescription,
          Carrier: this.batchCarrier,
          ServiceType: this.batchServiceType,
          Email: this.batchEmail,
          IsBatchEnabled: this.isBatchEnabled
        };

        // Toast message for ready-to-send
        Swal.fire({
          width: 200,
          toast: true,
          icon: 'success',
          title: this.translateService.instant("app.importOrders.readyToSend"),
          showConfirmButton: true,
          target: '#uploadToastTarget',
          customClass: { container: 'position-absolute' },
          timer: 2000,
          timerProgressBar: true,
          didOpen: (toast) => {
            const toastContainer = document.querySelector(".swal2-container") as HTMLElement;
            if (toastContainer) {
              const target = document.getElementById("uploadToastTarget");
              if (target) {
                const targetRect = target.getBoundingClientRect();
                toastContainer.style.top = `${targetRect.top}px`;
                toastContainer.style.left = `${targetRect.left}px`;
              }
            }
            toast.addEventListener('mouseenter', Swal.stopTimer);
            toast.addEventListener('mouseleave', Swal.resumeTimer);
          }
        });
      });
    } else {
      this.uploadProgress = 0;
      this.fileName = undefined;
    }

    event.target.value = "";
  }

  //-------------------------------------------------------------------
  // https://upmostly.com/angular/converting-files-to-base64-in-angular
  //-------------------------------------------------------------------
  convertFile(file: File): Observable<string> {
    const result = new ReplaySubject<string>(1);
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (event) => result.next(btoa(event.target!.result!.toString()));
    return result;
  }

  //----------------------------------------------------------
  // Action of uploading the file to the server for processing
  //----------------------------------------------------------
  upload(): void {
    if (!this.importOrdersDetail) {
      Swal.fire('', this.translateService.instant("app.importOrders.noFileSelected"), "info");
      return;
    }

    this.uploadProgress = 0;
    this.isProcessing = true;

    // 🔹 include all values already inside importOrdersDetail
    this.httpClient.post<ImportResult>(
      environment.CSDAEndPoint + "/CSDA/UploadOrders",
      this.importOrdersDetail
    )
    .pipe(
      map(data => {
        const validation = this.validationService.validate(data);

        if (validation.isValid === false) {
          Swal.fire('', validation.message, "error");
          return null;
        }

        if (validation.message !== "") {
          Swal.fire('', validation.message, "info");
        } else {
          Swal.fire('', this.translateService.instant("app.importOrders.fileUploaded"), "info");
          this.uploadProgress = 0;
          this.fileName = undefined;
        }

        this.isProcessing = false;
      })
    )
    .subscribe({
      next: () => {
        this.isProcessing = false;
        this.fileName = undefined;
      },
      error: (err) => {
        this.isProcessing = false;
        console.error('Upload error:', err);
        Swal.fire('', err.message, "error");
      }
    });
  }
}
